import { Component } from '@angular/core';

import { PatchReservationPayload, Reservation } from '../../reservation.model';

import { DateService } from '@/common/date.service';
import { InfoboxService } from '@/common/infobox/infobox.service';
import { DataBagService } from '@/common/data-bag.service';
import { ReservationService } from '../../reservation.service';
import { ReservationSummaryCommonService } from '../services/reservation-summary-common.service';
import { CancellationStatuses, Status, statusMappings } from '@/common/enums/Status';
import { ReservationSummaryApiService } from '../services/reservation-summary-api.service';
import { DateAdapter } from '@angular/material/core';
import { LoaderService } from '@/common/loader/loader.service';
import { DatePipe } from '@angular/common';
import { AlertType } from '@/common/enums/AlertType';
import { BehaviorSubject, combineLatest, map, Observable, take, tap } from 'rxjs';

@Component({
	selector: 'app-reservation-summary-status',
	templateUrl: './reservation-summary-status.component.html',
	styleUrls: ['./reservation-summary-status.component.scss'],
	providers: [DatePipe],
})
export class ReservationSummaryStatusComponent {
	reservationStatus = '';
	selectedDate: Date = new Date();
	selectedTime: string = null;
	alertsAmount: number = null;
	reservation$: BehaviorSubject<Reservation> = this._service.reservation$;
	timeOpened = false;

	constructor(
		private readonly dateService: DateService,
		private readonly infobox: InfoboxService,
		private readonly api: DataBagService,
		private readonly _service: ReservationService,
		private readonly commonService: ReservationSummaryCommonService,
		private readonly dateAdapter: DateAdapter<Date>,
		private readonly loader: LoaderService,
		private readonly apiService: ReservationSummaryApiService,
	) {}

	reservationStatus$ = this.reservation$.pipe(map((reservation) => statusMappings[reservation.status] || ''));

	amountOfOptionTimeChanges$ = this.reservation$.pipe(
		map(
			(reservation) =>
				reservation.alerts.filter((alert) => alert.alert.id === AlertType.OPTION_TERM_CHANGED).length,
		),
	);

	reservationTime$ = this.reservation$.pipe(
		map((reservation) => {
			if (!reservation?.reservationTime) return 'Brak danych';

			const reservationTime = this.dateService.apiToDateTime(reservation?.reservationTime);

			return `${reservationTime.date} | ${reservationTime.time}`;
		}),
	);

	isReservationCancelled$ = this.reservation$.pipe(
		map((reservation) => CancellationStatuses.includes(reservation.status)),
	);

	formattedSelectedDate$ = this.reservation$.pipe(
		map(() => {
			return this.selectedDate ? this.dateService.datetimeWithTimezone(this.selectedDate).date : '';
		}),
	);

	statusTime$: Observable<string> = this.reservation$.pipe(
		map((reservation) => {
			switch (reservation.status) {
				case Status.OPTION:
				case Status.WAIT_FOR_DECISION:
					return reservation.expirationTime;
				case Status.CONFIRMED:
					return reservation.confirmationTime;
				case Status.CANCELED_BY_CLIENT:
				case Status.CANCELED_OBJECT:
				case Status.CANCELED:
				case Status.CANCELED_TERM:
				case Status.CANCELED_WITHOUT_COST:
				case Status.CANCELED_MULTIPLE:
				case Status.REJECTED:
				case Status.REJECTED_EXPIRATION:
				case Status.REJECTED_CLIENT:
				case Status.REJECTED_OBJECT:
				case Status.REJECTED_TERM:
				case Status.REJECTED_PROMOTION:
				case Status.REJECTED_SELLER:
					return reservation.canceledAt;
				default:
					const value: never = reservation.status;
					throw new Error(`There is no value like: ${value}`);
			}
		}),
	);

	confirmReservation() {
		this.reservation$
			.pipe(
				take(1),
				tap((reservation: Reservation) => {
					const payload: PatchReservationPayload = {
						id: reservation.id,
						status: Status.CONFIRMED,
						confirmationTime: this.dateService.now().full,
						...(reservation.isOnRequest && { isOnRequest: false }),
					};
					this.updateReservation(payload, 'Pomyśnie zatwierdzono rezerwacje');
				}),
			)
			.subscribe();
	}

	private updateReservation(payload: PatchReservationPayload, successMessage: string) {
		this.loader.show();
		this.api.patch(`/reservation/${payload.id}/`, payload, (_, { error }) => {
			if (error) return;
			else {
				this.infobox.success(successMessage);
				this.apiService.actions().getReservation(payload.id);
			}
		});
	}

	changeOptionTime() {
		combineLatest([this.reservation$, this.formattedSelectedDate$])
			.pipe(
				take(1),
				tap(([reservation, formattedSelectedDate]) => {
					const newOptionTime = formattedSelectedDate + ' ' + this.selectedTime;

					const newIso = this.dateService.toUtcIso(newOptionTime);
					const reservationLowerDateIso = this.dateService.toUtcIso(reservation.time.lower);

					const isInvalidReservationTime = this.dateService.daysBetween(newIso, reservationLowerDateIso) <= 0;

					if (isInvalidReservationTime && !CancellationStatuses.includes(reservation.status)) {
						return this.infobox.error('Czas opcji nie może następować po wyjeździe');
					}

					const expirationTime = newIso;

					const payload: PatchReservationPayload = {
						id: reservation.id,
						status: Status.OPTION,
						expirationTime,
					};

					this.updateReservation(payload, 'Pomyślnie zmieniono czas opcji!');
					this.timeOpened = false;
				}),
			)
			.subscribe();
	}

	showCancelDialog() {
		this.reservation$
			.pipe(
				take(1),
				tap((reservation) => {
					this.commonService.showCancelDialog({
						reservationId: reservation?.id,
						offerId: null,
						slotId: null,
					});
				}),
			)
			.subscribe();
	}

	dateFilter = (date: Date): boolean => {
		// dateFilter directive does not support async pipes, so I need to do it this way
		const reservation = this.reservation$.getValue();
		if (CancellationStatuses.includes(reservation.status)) return true;
		return (
			this.dateAdapter.compareDate(date, new Date()) >= 0 &&
			this.dateAdapter.compareDate(date, new Date(reservation.time.lower)) <= 0
		);
	};

	readonly statusMappings = statusMappings;
	readonly Status = Status;
}
